/**
 * ツールバー／Hero の CSV・貼付を、t検定の入力形式（2列ワイド vs 長形式）へ正しく載せる。
 */

import type { DataRow, InputFormTypeKey, VariableMapping } from '../../../../stores/systems/keisuke/dashboardStore';
import { normalizeKeisukeNumericInput } from '../../../../utils/keisukeInputFormUtils';

const GROUP_KEY = 'group';
const VALUE_KEY = 'value';

function cellLooksNumeric(raw: unknown): boolean {
  const s = normalizeKeisukeNumericInput(String(raw ?? '').trim());
  if (!s) return false;
  return Number.isFinite(Number(s.replace(/,/g, '')));
}

function parseNumberCell(raw: unknown): number | null {
  const s = normalizeKeisukeNumericInput(String(raw ?? '').trim());
  if (!s) return null;
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * 2列が tidy（群ラベル＋測定値）か。ヘッダ名ヒューリスティック＋セルの型分布。
 */
export function tabularTwoColumnsLookTidy(
  headers: string[],
  rows: Array<Record<string, unknown>>
): boolean {
  if (headers.length !== 2) return false;
  const h0 = headers[0]!;
  const h1 = headers[1]!;
  const h0l = h0.toLowerCase();
  const h1l = h1.toLowerCase();
  if (
    /^(group|condition|genotype|treatment|strain|arm|群|条件|処理)/i.test(h0l) &&
    /^(value|weight|measure|response|y|値|体重|測定)/i.test(h1l)
  ) {
    return true;
  }

  let catHits = 0;
  let numHits = 0;
  let considered = 0;
  for (const row of rows) {
    const a = String(row[h0] ?? '').trim();
    const b = String(row[h1] ?? '').trim();
    if (!a && !b) continue;
    considered += 1;
    if (a && !cellLooksNumeric(a)) catHits += 1;
    if (b && cellLooksNumeric(b)) numHits += 1;
    if (considered >= 40) break;
  }
  if (considered < 2) return false;
  const need = Math.ceil(considered * 0.6);
  return catHits >= need && numHits >= need;
}

export type TTestImportResolution = {
  headers: string[];
  data: DataRow[];
  inputFormType: Extract<InputFormTypeKey, 'longFormat' | 'tTestTwoColumns'>;
  mapping: VariableMapping;
};

/**
 * サンプル読込・CSV/貼付共通: t検定向けに表の形を判定し、ストアに載せる形へ正規化する。
 * - tidy 長形式（群+値）→ 列はそのまま、UI は tTestTwoColumns（2列表が長形式から同期）
 * - 対応なしワイド2列 → group/value 長形式＋tTestTwoColumns
 * - 対応ありワイド2列 → before/after のまま tTestTwoColumns
 * - 3列以上など → longFormat（列マッピング UI）
 */
export function resolveTTestTabularImport(args: {
  headers: string[];
  rows: DataRow[];
  testType: 'unpaired' | 'paired';
  /** サンプルの suggested mapping（あれば優先） */
  suggestedMapping?: VariableMapping | null;
}): TTestImportResolution {
  const { headers, rows, testType, suggestedMapping } = args;
  const map = suggestedMapping ?? undefined;

  const mappingSaysLong =
    Boolean(map?.groupColumn && map?.valueColumn) &&
    headers.includes(String(map!.groupColumn)) &&
    headers.includes(String(map!.valueColumn)) &&
    !(map?.beforeColumn && map?.afterColumn);

  const mappingSaysPairedWide =
    Boolean(map?.beforeColumn && map?.afterColumn) &&
    headers.includes(String(map!.beforeColumn)) &&
    headers.includes(String(map!.afterColumn));

  if (mappingSaysLong || (headers.length === 2 && tabularTwoColumnsLookTidy(headers, rows))) {
    const g = String(map?.groupColumn ?? headers[0]!);
    const v = String(map?.valueColumn ?? headers[1]!);
    return {
      headers,
      data: rows,
      inputFormType: 'tTestTwoColumns',
      mapping: {
        groupColumn: g,
        valueColumn: v,
        ...(v && v.toLowerCase() !== 'value' ? { valueAxisLabel: v } : {}),
      },
    };
  }

  if (testType === 'paired' || mappingSaysPairedWide) {
    const before = String(map?.beforeColumn ?? headers[0] ?? 'Before');
    const after = String(map?.afterColumn ?? headers[1] ?? 'After');
    return {
      headers,
      data: rows,
      inputFormType: 'tTestTwoColumns',
      mapping: { beforeColumn: before, afterColumn: after },
    };
  }

  /** 対応なし・ワイド2列（群A | 群B）→ 内部正の group/value へ展開 */
  if (headers.length === 2) {
    const a = headers[0]!;
    const b = headers[1]!;
    const data: DataRow[] = [];
    for (const row of rows) {
      const va = parseNumberCell(row[a]);
      const vb = parseNumberCell(row[b]);
      if (va !== null) data.push({ [GROUP_KEY]: a, [VALUE_KEY]: va });
      if (vb !== null) data.push({ [GROUP_KEY]: b, [VALUE_KEY]: vb });
    }
    return {
      headers: [GROUP_KEY, VALUE_KEY],
      data: data.length > 0 ? data : rows,
      inputFormType: 'tTestTwoColumns',
      mapping: { groupColumn: GROUP_KEY, valueColumn: VALUE_KEY },
    };
  }

  return {
    headers,
    data: rows,
    inputFormType: 'longFormat',
    mapping: {
      groupColumn: headers[0] ?? GROUP_KEY,
      valueColumn: headers[1] ?? VALUE_KEY,
    },
  };
}
